"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CoralLegend = void 0;
var d3 = require('d3');
const React = require("react");
const react_redux_1 = require("react-redux");
const vega_tooltip_1 = require("vega-tooltip");
const BarChart_js_1 = require("./BarChart.js");
const VegaHist_js_1 = require("./VegaHist.js");
const VegaDate_js_1 = require("./VegaDate.js");
const styles_1 = require("@material-ui/core/styles");
const Table_1 = require("@material-ui/core/Table");
const TableBody_1 = require("@material-ui/core/TableBody");
const TableCell_1 = require("@material-ui/core/TableCell");
const TableContainer_1 = require("@material-ui/core/TableContainer");
const TableHead_1 = require("@material-ui/core/TableHead");
const TableRow_1 = require("@material-ui/core/TableRow");
const Paper_1 = require("@material-ui/core/Paper");
require("./coral.scss");
const datasetselector_1 = require("../../../components/util/datasetselector");
const useStyles = styles_1.makeStyles({
    table: {
        maxWidth: 288,
    },
});
function createData(feature, category, score, char) {
    return { feature, category, score, char };
}
function mapHistData(data, feature) {
    const mapped = data.map((d) => {
        return {
            feature: +d[feature]
        };
    });
    return { "values": mapped };
}
function mapBarChartData(data, feature) {
    const counts = {};
    for (var i = 0; i < data.length; i++) {
        if (data[i][feature] in counts) {
            counts[data[i][feature]] += 1;
        }
        else {
            counts[data[i][feature]] = 1;
        }
    }
    const sortCountDesc = (a, b) => {
        return b['count'] - a['count'];
    };
    const barChartData = [];
    for (var key in counts) {
        barChartData.push({ 'category': key, 'count': counts[key] / data.length });
    }
    barChartData.sort(sortCountDesc);
    return { 'values': barChartData };
}
const getSTD = (data) => {
    const total = data.reduce(function (a, b) {
        return a + b;
    });
    const mean = total / data.length;
    function var_numerator(value) {
        return ((value - mean) * (value - mean));
    }
    var variance = data.map(var_numerator);
    variance = variance.reduce(function (a, b) {
        return (a + b);
    });
    variance = variance / data.length;
    const std = Math.sqrt(variance);
    return std;
};
function dictionary(list) {
    var map = {};
    for (var i = 0; i < list.length; ++i) {
        for (var key in list[i]) {
            if (key in map) {
                map[key].push(list[i][key]);
            }
            else {
                map[key] = [list[i][key]];
            }
        }
    }
    return map;
}
function getMaxMean(data) {
    var max = Number.NEGATIVE_INFINITY;
    data = data['values'];
    for (var i = 0; i < data.length; i++) {
        if (data[i]['count'] > max) {
            max = data[i]['count'];
        }
    }
    return max;
}
function sortByScore(a, b) {
    if (a['score'] === b['score']) {
        return 0;
    }
    else {
        return (a['score'] < b['score']) ? 1 : -1;
    }
}
function getExplainingFeatures(data) {
    // data format [{'category': x, 'count': y}, ...]
    // data should be sorted descendingly
    const n = data.length;
    const features = [];
    for (var i = 0; i < n; i++) {
        if (data[i]['count'] >= (1 / n)) {
            features.push(data[i]['category']);
        }
        else {
            return features;
        }
    }
    return features;
}
function getProjectionColumns(projectionColumns) {
    if (projectionColumns === null) {
        return [];
    }
    const pcol = [];
    for (var i = 0; i <= projectionColumns.length; i++) {
        if (projectionColumns[i] !== undefined && projectionColumns[i]['checked']) {
            pcol.push(projectionColumns[i]['name']);
        }
    }
    return pcol;
}
function getNormalizedSTD(data, min, max) {
    if (min === max) {
        return 0;
    }
    data.forEach((x, i, self) => {
        self[i] = (+x - +min) / (+max - +min);
    });
    return getSTD(data);
}
function genRows(vectors, projectionColumns, dataset) {
    var _a, _b, _c;
    if (dataset === undefined) {
        return [];
    }
    const rows = [];
    const dictOfArrays = dictionary(vectors);
    const preselect = getProjectionColumns(projectionColumns);
    // loop through dict
    for (var key in dictOfArrays) {
        // filter for preselect features
        if (preselect.indexOf(key) > -1) {
            if (((_a = dataset.columns[key]) === null || _a === void 0 ? void 0 : _a.featureType) === datasetselector_1.FeatureType.Quantitative) {
                // quantitative feature
                var histData = mapHistData(vectors, key);
                rows.push([key, "", 1 - getNormalizedSTD(dictOfArrays[key], dataset.columns[key].range.min, dataset.columns[key].range.max), React.createElement(VegaHist_js_1.default, { data: histData, actions: false, tooltip: new vega_tooltip_1.Handler().call })]);
            }
            else if (((_b = dataset.columns[key]) === null || _b === void 0 ? void 0 : _b.featureType) === datasetselector_1.FeatureType.Categorical) {
                // categorical feature
                var barData = mapBarChartData(vectors, key);
                rows.push([key, barData['values'][0]['category'], getMaxMean(barData), React.createElement(BarChart_js_1.default, { data: barData, actions: false, tooltip: new vega_tooltip_1.Handler().call })]);
            }
            else if (((_c = dataset.columns[key]) === null || _c === void 0 ? void 0 : _c.featureType) === datasetselector_1.FeatureType.Date) {
                // date feature
                var histData = mapHistData(vectors, key);
                rows.push([key, "", 1 - getNormalizedSTD(dictOfArrays[key], dataset.columns[key].range.min, dataset.columns[key].range.max), React.createElement(VegaDate_js_1.default, { data: histData, actions: false, tooltip: new vega_tooltip_1.Handler().call })]);
            }
        }
    }
    // turn into array of dicts
    const ret = [];
    for (var i = 0; i < rows.length; i++) {
        ret.push(createData(rows[i][0], rows[i][1], rows[i][2], rows[i][3]));
    }
    // sort rows by score
    ret.sort(sortByScore);
    return ret;
}
function getTable(vectors, aggregation, projectionColumns, dataset) {
    const classes = useStyles();
    const rows = genRows(vectors, projectionColumns, dataset);
    return (React.createElement("div", null,
        React.createElement(TableContainer_1.default, { component: Paper_1.default, style: {
                height: "400px",
                width: "100%",
                overflow: "auto"
            } },
            React.createElement(Table_1.default, { className: classes.table, "aria-label": "simple table", size: 'small' },
                React.createElement(TableHead_1.default, null,
                    React.createElement(TableRow_1.default, null,
                        React.createElement(TableCell_1.default, null, "Feature"),
                        React.createElement(TableCell_1.default, null, "Characteristic"))),
                React.createElement(TableBody_1.default, null, rows.map((row) => (React.createElement(TableRow_1.default, { key: row.feature },
                    React.createElement(TableCell_1.default, { component: "th", scope: "row" },
                        row.feature,
                        React.createElement("br", null),
                        React.createElement("b", null, row.category)),
                    React.createElement(TableCell_1.default, null, row.char)))))))));
}
const mapState = state => {
    return ({
        projectionColumns: state.projectionColumns,
        dataset: state.dataset
    });
};
const mapDispatch = dispatch => ({});
const connector = react_redux_1.connect(mapState, mapDispatch);
exports.CoralLegend = connector(({ selection, aggregate, projectionColumns, dataset }) => {
    return getTable(selection, aggregate, projectionColumns, dataset);
});
